import { get, readable, writable } from "svelte/store";
import { reload } from "./ReloadService";
import { reloadService } from ".";
import { State } from "../Model/State";
export class RealTimeService {
    constructor(serviceEndpoint) {
        this.serviceEndpoint = serviceEndpoint;
        this.signal = new State('~');
        this.clientId = null;
        this.roomId = null;
        this.reconnectTime = 5000;
        this.pingPongTime = 20 * 1000;
        this._isOpen = writable(false);
        this.handlersMap = new Map();
        this.isOpen = readable(false, set => this._isOpen.subscribe(set));
    }
    start(clientId, roomId) {
        if (this.clientId || this.roomId) {
            return;
        }
        this.roomId = roomId;
        this.clientId = clientId;
        this.connect();
    }
    end() {
        var _a;
        this.clientId = null;
        this.roomId = null;
        (_a = this.ws) === null || _a === void 0 ? void 0 : _a.close();
    }
    subscribe(eventPattern, handler) {
        if (!this.handlersMap.has(eventPattern)) {
            this.handlersMap.set(eventPattern, this.createEventHandlers(eventPattern));
        }
        this.handlersMap.get(eventPattern).handlers.add(handler);
        this.send('subscribe', eventPattern);
        return () => this.unsubscribe(eventPattern, handler);
    }
    unsubscribe(eventPattern, handler) {
        if (!this.handlersMap.has(eventPattern)) {
            return;
        }
        const { handlers } = this.handlersMap.get(eventPattern);
        handlers.delete(handler);
        if (handlers.size === 0) {
            this.handlersMap.delete(eventPattern);
            this.send('unsubscribe', eventPattern);
        }
    }
    createPatternRegexp(eventPattern) {
        const wildcarded = eventPattern
            .replace(/\*{2}/g, '[\\w\\.-]+')
            .replace(/\*/g, '[\\w-]+');
        return new RegExp(`^${wildcarded}$`);
    }
    send(...args) {
        try {
            if (!get(this._isOpen)) {
                return;
            }
            this.ws.send(JSON.stringify(args));
        }
        catch (error) {
            console.log(error);
        }
    }
    connect() {
        if (!this.clientId || !this.roomId || this.ws) {
            return;
        }
        this.ws = new WebSocket(`${this.serviceEndpoint}?clientId=${this.clientId}&roomId=${this.roomId}`);
        this.ws.addEventListener('open', () => this.onOpen());
        this.ws.addEventListener('close', () => this.onClose());
        this.ws.addEventListener('message', event => this.onMessage(event));
    }
    onOpen() {
        console.log('[Connection] Connection is open');
        reload.set(false);
        this._isOpen.set(true);
        this.subscribeToAllEvents();
        this.startPingPong();
    }
    async onClose() {
        console.log('[Connection] Connection is closed');
        this._isOpen.set(false);
        this.ws = null;
        this.reconnect();
        this.stopPingPong();
        if (this.checkPingPong()) {
            console.log('reloading');
            if (sessionStorage.getItem('reload')) {
                reload.set(true);
            }
            else {
                sessionStorage.setItem('reload', 'true');
                await reloadService.saveReload();
                location.reload();
            }
        }
    }
    onMessage(messageEvent) {
        try {
            if (messageEvent.data === 'pong') {
                console.log('pong made successfully');
                localStorage.setItem('pong', JSON.stringify(new Date()));
                const ping = new Date(JSON.parse(localStorage.getItem('ping')));
                const pong = new Date(JSON.parse(localStorage.getItem('pong')));
                this.signal.set(this.calculatePing(pong.getTime() - ping.getTime()));
                return;
            }
            const event = JSON.parse(messageEvent.data);
            for (const { regexp, handlers } of this.handlersMap.values()) {
                if (regexp.test(event.name)) {
                    handlers.forEach(handler => handler(event));
                }
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    reconnect() {
        window.clearTimeout(this.reconnectTimeout);
        if (!this.clientId || !this.roomId) {
            return;
        }
        this.reconnectTimeout = window.setTimeout(() => {
            const pong = new Date(JSON.parse(localStorage.getItem('pong')));
            this.signal.set(this.calculatePing(new Date().getTime() - pong.getTime()));
            this.connect();
        }, this.reconnectTime);
    }
    subscribeToAllEvents() {
        if (!get(this._isOpen)) {
            return;
        }
        for (const eventPattern of this.handlersMap.keys()) {
            this.send('subscribe', eventPattern);
        }
    }
    createEventHandlers(eventPattern) {
        return {
            regexp: this.createPatternRegexp(eventPattern),
            handlers: new Set(),
        };
    }
    startPingPong() {
        if (this.pingPongInterval) {
            return;
        }
        localStorage.setItem('ping', JSON.stringify(new Date()));
        this.pingPongInterval = window.setInterval(() => this.sendPing(), this.pingPongTime);
    }
    stopPingPong() {
        if (!this.pingPongInterval) {
            return;
        }
        window.clearInterval(this.pingPongInterval);
        this.pingPongInterval = undefined;
    }
    sendPing() {
        var _a;
        if (((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) !== WebSocket.OPEN) {
            this.stopPingPong();
            return;
        }
        if (localStorage.getItem('pong')) {
            if (this.checkPingPong()) {
                localStorage.removeItem('pong');
                location.reload();
            }
            else {
                this.ws.send('ping');
                localStorage.setItem('ping', JSON.stringify(new Date()));
            }
        }
        else {
            this.ws.send('ping');
            localStorage.setItem('ping', JSON.stringify(new Date()));
        }
    }
    checkPingPong() {
        const pongDate = new Date(JSON.parse(localStorage.getItem('pong'))).getTime();
        const pingDate = new Date(JSON.parse(localStorage.getItem('ping'))).getTime();
        const today = new Date().getTime();
        if ((today - pongDate) > (30 * 1000) || (today - pingDate) > (30 * 1000)) {
            this.signal.set(this.calculatePing(pongDate - pingDate));
            return true;
        }
        return false;
    }
    calculatePing(ping) {
        console.log(ping);
        if (ping > 0 && ping < 80) {
            return '++';
        }
        if (ping > 80 && ping < 110) {
            return '+';
        }
        if (ping > 110 && ping < 150) {
            return '+-';
        }
        if (ping > 150 && ping < 200) {
            return '-';
        }
        if (ping > 200) {
            return '--';
        }
    }
}
